import { ADVISORS, katLabel, VERANTWORTUNG_LABEL } from "../state/schema";
import {
  card, checkbox, h, moneyInput, note, optionCards, slider, textareaInput,
} from "../components/ui";
import { uploadBlock } from "../components/upload";
import { advisorLabel } from "../logic/advisors";
import { berechneHaushalt } from "../logic/calc";
import { formatEUR } from "../logic/format";
import {
  investitionsWarnung, vBerater, vEinwilligungen, vPraeferenzen, vZukunft,
} from "../logic/validation";
import { Step } from "./types";

/* ---------- 15 · Futuro y margen de inversión ---------- */
export const stepZukunft: Step = {
  id: "zukunft",
  section: "zukunft",
  eyebrow: "Futuro",
  title: "Tu futuro en la mira",
  subtitle: "¿Qué quieres planificar para tu futuro?",
  leaveMessage: "Casi listo: ahora tu futuro.",
  validate: vZukunft,
  build(ctx) {
    const s = ctx.store;
    const hh = berechneHaushalt(s.state);
    const frei = hh.freiVerfuegbar;
    const empfMin = Math.round(frei * 0.5);
    const empfMax = Math.round(frei * 0.8);

    const warnBox = h("div");
    const updateWarn = () => {
      warnBox.innerHTML = "";
      const w = investitionsWarnung(s.state);
      if (w) warnBox.appendChild(note("warn", w));
    };

    const kv = (k: string, v: string) =>
      h("div", { class: "kv__row" },
        h("span", { class: "kv__k", style: { color: "var(--on-card-muted)" }, text: k }),
        h("span", { class: "kv__v", style: { color: "var(--on-card)" }, text: v }));

    const block = card(
      h("div", { class: "card__title", text: "Tomado de tu análisis" }),
      h("div", { class: "kv" },
        kv("Ingresos totales", formatEUR(hh.einnahmenGesamt)),
        kv("Obligaciones corrientes", formatEUR(hh.laufendeVerbindlichkeiten)),
        kv(frei < 0 ? "Déficit mensual" : "Disponible", formatEUR(frei))),
      h("p", { class: "field__optional", text: "Estos valores provienen de tus datos anteriores y no hace falta volver a introducirlos aquí." })
    );

    const empfehlung = frei > 0
      ? card(
          h("div", { class: "card__title", text: "★ Nuestra recomendación experta" }),
          h("p", { class: "card__hint", text: `De tu importe disponible de ${formatEUR(frei)} te recomendamos destinar a tu futuro entre ${formatEUR(empfMin)} (50 %) y ${formatEUR(empfMax)} (80 %) al mes.` }),
          note("info-dark", "Cuanto más te acerques a esta recomendación, antes alcanzarás tus objetivos y sueños. Los valores son una sugerencia: puedes ajustarlos cuando quieras."),
          h("button", { type: "button", class: "btn-add", text: "Usar la recomendación", on: { click: () => { s.set("zukunft.mindestens", String(empfMin)); s.set("zukunft.komfortabel", String(empfMax)); ctx.rerender(); } } })
        )
      : note("info-dark", "Ahora mismo no hay un importe disponible, y no es un problema. Tu asesor te mostrará dónde surge margen para tu futuro.");

    const input = card(
      h("div", { class: "card__title", text: "¿Qué importe quieres invertir en el futuro para hacer tus sueños realidad?" }),
      moneyInput(s, "zukunft.mindestens", { label: "Mínimo (mensual)", required: true, hint: frei > 0 ? `Recomendación: ${formatEUR(empfMin)} (50 % de tu importe disponible).` : undefined, onInput: updateWarn }),
      moneyInput(s, "zukunft.komfortabel", { label: "Máximo (mensual)", required: true, hint: frei > 0 ? `Recomendación: ${formatEUR(empfMax)} (80 % de tu importe disponible).` : "Lo máximo que te imaginas apartar para tus objetivos y sueños.", onInput: updateWarn }),
      moneyInput(s, "zukunft.einmalig", { label: "Importe disponible para invertir de una sola vez", required: true, hint: "También es posible 0 €." }),
      warnBox
    );
    updateWarn();
    return h("div", {}, block, empfehlung, input);
  },
};

/* ---------- 16 · Responsabilidad, riesgo, flexibilidad, preferencia ---------- */
export const stepPraeferenzen: Step = {
  id: "praeferenzen",
  section: "zukunft",
  eyebrow: "Futuro",
  title: "Tu actitud ante las inversiones",
  subtitle: "No hay respuestas correctas ni incorrectas.",
  validate: vPraeferenzen,
  build(ctx) {
    const s = ctx.store;

    const risikoText = (v: number) =>
      v <= 3 ? "La seguridad es especialmente importante para ti. Prefieres evitar grandes fluctuaciones."
        : v <= 6 ? "Puedes aceptar ciertas fluctuaciones si a largo plazo hay mejores oportunidades."
          : "Puedes aceptar fluctuaciones más marcadas y mantener la calma a largo plazo.";
    const flexText = (v: number) =>
      v <= 3 ? "Te imaginas planificar tu dinero a largo plazo."
        : v <= 6 ? "Valoras un equilibrio entre planificación y flexibilidad."
          : "Quieres mantener la máxima flexibilidad y poder acceder a tu dinero rápidamente.";

    return h("div", {},
      card(
        h("div", { class: "card__title", text: "¿Te sientes listo/a para asumir toda la responsabilidad y todos los riesgos de tus decisiones de inversión, o prefieres delegar esa responsabilidad?" }),
        optionCards(s, "verantwortung", [
          { value: "selbst", title: "Decido yo mismo/a", desc: "Quiero tomar mis decisiones por mí mismo/a y asumir los riesgos que conllevan." },
          { value: "unterstuetzung", title: "Quiero apoyo", desc: "Quiero tomar las decisiones junto con apoyo profesional." },
          { value: "abgeben", title: "Quiero delegar la responsabilidad", desc: "No quiero tener que asumir las decisiones y sus riesgos yo solo/a." },
        ])
      ),
      card(
        h("div", { class: "card__title", text: "¿Qué tan bien lo llevas si el valor de tu inversión baja temporalmente?" }),
        slider(s, "risiko", {
          left: "1 – muy incómodo/a", mid: "Tolerancia al riesgo",
          right: "10 – aguanto mucho",
        }, risikoText)
      ),
      card(
        h("div", { class: "card__title", text: "¿Qué importancia tiene para ti poder acceder en cualquier momento a tu dinero invertido?" }),
        slider(s, "flexibilitaet", {
          left: "1 – largo plazo ok", mid: "Flexibilidad",
          right: "10 – en cualquier momento",
        }, flexText)
      ),
      card(
        h("div", { class: "card__title", html: "¿Qué te resultaría más difícil de aceptar? <span style='color:var(--neg)'>*</span>" }),
        optionCards(s, "praeferenz", [
          { value: "kurzfristige_verluste", title: "Pérdidas a corto plazo" },
          { value: "geringe_ertraege", title: "Rendimientos demasiado bajos a largo plazo" },
          { value: "verfuegbarkeit", title: "Disponibilidad limitada" },
          { value: "falsche_entscheidung", title: "Haber tomado yo mismo/a una decisión equivocada" },
        ])
      )
    );
  },
};

/* ---------- 17 · Documentos y lista de verificación ---------- */
export const stepDokumente: Step = {
  id: "dokumente",
  section: "abschluss",
  eyebrow: "Cierre",
  title: "Documentos",
  subtitle: "Sube lo que tengas a mano.",
  build(ctx) {
    const s = ctx.store;
    const checklist = h("div", { class: "checklist" });
    const renderChecklist = () => {
      checklist.innerHTML = "";
      const items: { label: string; kat: string; pflicht: boolean }[] = [
        { label: "Comprobante de ingresos", kat: "Einkommensnachweis", pflicht: s.state.einkommensnachweis.art === "gehaltsabrechnung" || s.state.einkommensnachweis.art === "jahresendabrechnung" },
        { label: "Pólizas de seguro", kat: "Sachversicherung", pflicht: false },
        { label: "Factura de electricidad", kat: "Stromabrechnung", pflicht: false },
        { label: "Factura de gas", kat: "Gasabrechnung", pflicht: false },
        { label: "Documento de identidad", kat: "Personalausweis Vorderseite", pflicht: false },
      ];
      for (const it of items) {
        const docs = s.state.dokumente.filter((d) => d.kategorie === it.kat);
        const hasErr = docs.some((d) => d.status === "error");
        const hasDone = docs.some((d) => d.status === "done");
        let badge = it.pflicht ? "badge--open" : "badge--na";
        let txt = it.pflicht ? "pendiente" : "opcional";
        if (hasDone) { badge = "badge--done"; txt = "subido"; }
        else if (hasErr) { badge = "badge--err"; txt = "falló"; }
        else if (!it.pflicht && docs.length === 0) { badge = "badge--na"; txt = "no necesario"; }
        checklist.appendChild(h("div", { class: "checkitem" },
          h("span", { class: `checkitem__badge ${badge}`, text: txt }),
          h("span", { text: it.label })));
      }
    };
    const refresh = () => renderChecklist();
    renderChecklist();

    return h("div", {},
      card(h("div", { class: "card__title", text: "Lista de documentos" }), checklist),
      card(
        h("div", { class: "card__title", text: "Documento de identidad (opcional)" }),
        note("info", "Opcional: el anverso y el reverso nos ayudan con la identificación posterior y la preparación de solicitudes. También puedes terminar el Análisis de Valor sin copia del documento."),
        h("div", { class: "field__label", text: "Anverso" }),
        uploadBlock(s, { kategorie: "Personalausweis Vorderseite", multiple: false, onChange: refresh }),
        h("div", { class: "field__label", style: { marginTop: "12px" }, text: "Reverso" }),
        uploadBlock(s, { kategorie: "Personalausweis Rückseite", multiple: false, onChange: refresh })
      ),
      card(
        h("div", { class: "card__title", text: "Otro documento relevante (opcional)" }),
        uploadBlock(s, { kategorie: "Sonstige relevante Unterlage", onChange: refresh })
      )
    );
  },
};

/* ---------- 18 · Otras observaciones ---------- */
export const stepSonstiges: Step = {
  id: "sonstiges",
  section: "abschluss",
  eyebrow: "Cierre",
  title: "Otras observaciones",
  subtitle: "¿Hay algo más que debamos saber?",
  build(ctx) {
    return card(
      textareaInput(ctx.store, "sonstiges", {
        label: "¿Hay algo más que debamos saber antes de tu próxima asesoría?",
        optional: true, optionalText: "Opcional: complétalo solo si quieres contarnos algo más.",
        rows: 5, maxlength: 1500, placeholder: "Tu mensaje …",
      })
    );
  },
};

/* ---------- 19 · Selección de asesor ---------- */
export const stepBerater: Step = {
  id: "berater",
  section: "abschluss",
  eyebrow: "Cierre",
  title: "Tu asesor personal",
  subtitle: "¿Quién te atiende?",
  validate: vBerater,
  build(ctx) {
    return card(
      h("div", { class: "card__title", html: "¿Quién es tu asesor personal? <span style='color:var(--neg)'>*</span>" }),
      optionCards(ctx.store, "beraterId", ADVISORS.map((a) => ({ value: a.id, title: a.label })))
    );
  },
};

/* ---------- 20 · Resumen ---------- */
export const stepZusammenfassung: Step = {
  id: "zusammenfassung",
  section: "abschluss",
  eyebrow: "Cierre",
  title: "Resumen",
  subtitle: "Revisa tus datos antes de enviar.",
  validate: (s) => (s.einwilligungen.angabenGeprueft ? [] : ["Confirma que has revisado tus datos."]),
  build(ctx) {
    const s = ctx.store;
    const a = s.state;
    const hh = berechneHaushalt(a);
    const ja = (v: string) => (v === "ja" ? "Sí" : v === "nein" ? "No" : v === "weiss_nicht" ? "No lo sé" : "—");

    const sec = (title: string, stepId: string, rows: [string, string][]) =>
      h("div", { class: "summary-section" },
        h("div", { class: "summary-section__head" },
          h("div", { class: "summary-section__title", text: title }),
          h("button", { class: "btn-edit", type: "button", text: "Editar", on: { click: () => ctx.goTo(stepId) } })),
        h("div", { class: "kv" }, ...rows.map(([k, v]) =>
          h("div", { class: "kv__row" },
            h("span", { class: "kv__k", style: { color: "var(--on-card-muted)" }, text: k }),
            h("span", { class: "kv__v", style: { color: "var(--on-card)" }, text: v || "—" })))));

    const doneDocs = a.dokumente.filter((d) => d.status === "done");

    return h("div", {},
      card(
        sec("Datos personales", "persoenlich", [
          ["Nombre", `${a.stammdaten.vorname} ${a.stammdaten.nachname}`],
          ["Fecha de nacimiento", a.stammdaten.geburtsdatum],
          ["Dirección", `${a.stammdaten.strasse} ${a.stammdaten.hausnummer}, ${a.stammdaten.plz} ${a.stammdaten.ort}`],
          ["Correo", a.stammdaten.email],
          ["Móvil", a.stammdaten.mobil],
          ["Estado civil", a.stammdaten.familienstand],
          ["Hijos", a.stammdaten.hatKinder === "ja" ? `Sí (${a.stammdaten.anzahlKinder})` : ja(a.stammdaten.hatKinder)],
        ]),
        sec("Salud", "gesundheit", [
          ["Física", ja(a.gesundheit.koerperlich)],
          ["Mental", ja(a.gesundheit.psychisch)],
          ["Fumador/a", ja(a.gesundheit.raucher)],
          ["Pasatiempos de riesgo", a.gesundheit.risikohobby === "ja" ? `Sí – ${a.gesundheit.risikohobbyText}` : ja(a.gesundheit.risikohobby)],
        ]),
        sec("Previsión y seguros", "altersvorsorge", [
          ["Previsión de jubilación", `${ja(a.altersvorsorge.vorhanden)} (${a.altersvorsorge.vertraege.length})`],
          ["Capacidad laboral", `${ja(a.arbeitskraft.vorhanden)} (${a.arbeitskraft.vertraege.length})`],
          ["Seguros de bienes", a.sachversicherungen.auswahl.join(", ")],
          ["Patrimonio", `${ja(a.vermoegen.vorhanden)} (${a.vermoegen.posten.length} categorías)`],
        ]),
        sec("Energía y seguro médico", "strom", [
          ["Electricidad", a.strom.eigenerVertrag === "ja" ? `${a.strom.anbieter} · ${a.strom.abschlag} €` : ja(a.strom.eigenerVertrag)],
          ["Gas", a.gas.eigenerVertrag === "ja" ? `${a.gas.anbieter} · ${a.gas.abschlag} €` : ja(a.gas.eigenerVertrag)],
          ["Seguro médico", a.krankenversicherung.gesetzlichVersichert === "ja" ? a.krankenversicherung.anbieter : ja(a.krankenversicherung.gesetzlichVersichert)],
        ]),
        sec("Cálculo del hogar", "haushalt", [
          ["Ingresos totales", formatEUR(hh.einnahmenGesamt)],
          ["Obligaciones corrientes", formatEUR(hh.laufendeVerbindlichkeiten)],
          [hh.freiVerfuegbar < 0 ? "Déficit mensual" : "Disponible", formatEUR(hh.freiVerfuegbar)],
        ]),
        sec("Futuro y actitud", "zukunft", [
          ["Mínimo", `${a.zukunft.mindestens || "—"} €`],
          ["Máximo", `${a.zukunft.komfortabel || "—"} €`],
          ["Único", `${a.zukunft.einmalig || "—"} €`],
          ["Responsabilidad", VERANTWORTUNG_LABEL[a.verantwortung] || "—"],
          ["Riesgo", `${a.risiko} / 10`],
          ["Flexibilidad", `${a.flexibilitaet} / 10`],
        ]),
        sec("Documentos y asesor", "dokumente", [
          ["Subidos", doneDocs.length ? doneDocs.map((d) => katLabel(d.kategorie)).join(", ") : "ninguno"],
          ["Asesor", advisorLabel(a.beraterId)],
        ])
      ),
      card(
        checkbox(s, "einwilligungen.angabenGeprueft",
          "He revisado mis datos y los he indicado de forma completa y correcta según mi leal saber y entender.")
      )
    );
  },
};

/* ---------- 21 · Privacidad y envío ---------- */
export const stepDatenschutz: Step = {
  id: "datenschutz",
  section: "abschluss",
  eyebrow: "Cierre",
  title: "Privacidad y consentimiento",
  subtitle: "Antes de enviar necesitamos tu consentimiento.",
  validate: vEinwilligungen,
  build(ctx) {
    const s = ctx.store;

    return h("div", {},
      card(
        h("div", { class: "card__title", text: "Tus consentimientos" }),
        checkbox(s, "einwilligungen.datenschutz", linkLabel("He leído la ", "política de privacidad", "/legal/privacidad.html", ".")),
        checkbox(s, "einwilligungen.verarbeitung", "Acepto el tratamiento de mis datos para preparar mi asesoría."),
        checkbox(s, "einwilligungen.uebermittlung", "Acepto la transmisión de mis datos a Blacksmith Finance GmbH."),
        checkbox(s, "einwilligungen.weiterleitung", "Acepto el envío a mi asesor seleccionado."),
        checkbox(s, "einwilligungen.driveSpeicherung", "Acepto el almacenamiento de mis documentos subidos en el Google Drive protegido."),
        checkbox(s, "einwilligungen.gesundheit",
          "Consiento expresamente que se traten mis respuestas de sí/no sobre salud física y mental (categoría especial de datos personales).", true)
      ),
      h("p", { class: "field__optional", style: { textAlign: "center" } },
        linkLabel("", "Aviso legal", "/legal/aviso-legal.html", "  ·  "),
      )
    );
  },
};

function linkLabel(pre: string, linkText: string, href: string, post: string): HTMLElement {
  const span = h("span", {}, pre);
  span.appendChild(h("a", { href, text: linkText, attrs: { target: "_blank", rel: "noopener" } }));
  span.appendChild(document.createTextNode(post));
  return span;
}
